import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { StarRating } from './StarRating';
import { feedbackService } from '@/services/feedback.service';
import type { SubmitFeedbackPayload } from '@/types/feedback';
import toast from 'react-hot-toast';
import { Copy } from 'lucide-react';
import {
  FEEDBACK_EVENTS,
  FEEDBACK_FORM_SUBMITTED_PROPERTIES,
  FEEDBACK_POSTHOG_TEXT_MAX_LENGTH,
} from './feedbackEvents';

const HASHTAG = '#QuranAssistant_Review';
const FB_URL = 'https://www.facebook.com/RamadanLifeCenter';
const IG_URL = 'https://www.instagram.com/ramadan.life.center';

function FacebookBrandIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M13.5 9H15V6h-1.7c-2.1 0-3.3 1.4-3.3 3.5V11H8v3h2v8h3v-8h2.2l.5-3H13V9.7c0-.5.3-.7.5-.7Z" />
    </svg>
  );
}

function InstagramBrandIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 0 1 12 7.5Zm0 2A2.5 2.5 0 1 0 14.5 12 2.5 2.5 0 0 0 12 9.5Zm5.75-2.4a1.1 1.1 0 1 1-1.1 1.1 1.1 1.1 0 0 1 1.1-1.1Z" />
    </svg>
  );
}

const QUESTIONS = [
  {
    key: 'rating_overall' as const,
    label: 'การเปิดหน้ากุรอานและการเลื่อนอ่าน ทำได้ลื่นไหลเพียงใด?',
  },
  {
    key: 'rating_settings' as const,
    label: 'การปรับแต่ง (เช่น ขนาดอักษร/ตัจวีด) หาปุ่มง่ายและสะดวกไหม?',
  },
  {
    key: 'rating_translation' as const,
    label: 'การเปิดดูคำแปลในแต่ละอายะฮ์ ทำได้รวดเร็วและไม่ขัดจังหวะไหม?',
  },
  {
    key: 'rating_save_progress' as const,
    label: 'ขั้นตอนการ "บันทึกความก้าวหน้า" ใช้เวลาน้อยและสะดวกเพียงใด?',
  },
  {
    key: 'rating_recalc_feature' as const,
    label: 'ฟีเจอร์ "คำนวณแผนการอ่าน" ช่วยให้รักษาเป้าหมายได้ดีขึ้นไหม?',
  },
  {
    key: 'rating_read_more' as const,
    label: 'แอปนี้ทำให้พี่น้องอ่านกุรอาน "บ่อยขึ้น/สม่ำเสมอขึ้น" กว่าเดิมไหม?',
  },
];

export interface FeedbackFormProps {
  source?: string | null;
}

export function FeedbackForm({ source = null }: Readonly<FeedbackFormProps>) {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const [ratingOverall, setRatingOverall] = useState(0);
  const [ratingSettings, setRatingSettings] = useState(0);
  const [ratingTranslation, setRatingTranslation] = useState(0);
  const [ratingSaveProgress, setRatingSaveProgress] = useState(0);
  const [ratingRecalc, setRatingRecalc] = useState(0);
  const [ratingReadMore, setRatingReadMore] = useState(0);
  const [feedbackDifficult, setFeedbackDifficult] = useState('');
  const [feedbackFeatures, setFeedbackFeatures] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (posthog && !submitted) {
      posthog.capture(FEEDBACK_EVENTS.FORM_VIEWED, { source: source ?? undefined });
    }
  }, [posthog, submitted, source]);

  const canSubmit =
    ratingOverall >= 1 &&
    ratingOverall <= 4 &&
    ratingSettings >= 1 &&
    ratingSettings <= 4 &&
    ratingTranslation >= 1 &&
    ratingTranslation <= 4 &&
    ratingSaveProgress >= 1 &&
    ratingSaveProgress <= 4 &&
    ratingRecalc >= 1 &&
    ratingRecalc <= 4 &&
    ratingReadMore >= 1 &&
    ratingReadMore <= 4;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload: SubmitFeedbackPayload = {
        rating_overall: ratingOverall,
        rating_settings: ratingSettings,
        rating_translation: ratingTranslation,
        rating_save_progress: ratingSaveProgress,
        rating_recalc_feature: ratingRecalc,
        rating_read_more: ratingReadMore,
        feedback_difficult: feedbackDifficult.trim() || undefined,
        feedback_features: feedbackFeatures.trim() || undefined,
        source: source || undefined,
      };
      await feedbackService.submitFeedback(payload);
      if (posthog) {
        const truncate = (s: string | undefined) =>
          s ? s.slice(0, FEEDBACK_POSTHOG_TEXT_MAX_LENGTH) : undefined;
        posthog.capture(FEEDBACK_EVENTS.FORM_SUBMITTED, {
          [FEEDBACK_FORM_SUBMITTED_PROPERTIES.RATING_OVERALL]: payload.rating_overall,
          [FEEDBACK_FORM_SUBMITTED_PROPERTIES.RATING_SETTINGS]: payload.rating_settings,
          [FEEDBACK_FORM_SUBMITTED_PROPERTIES.RATING_TRANSLATION]: payload.rating_translation,
          [FEEDBACK_FORM_SUBMITTED_PROPERTIES.RATING_SAVE_PROGRESS]: payload.rating_save_progress,
          [FEEDBACK_FORM_SUBMITTED_PROPERTIES.RATING_RECALC_FEATURE]: payload.rating_recalc_feature,
          [FEEDBACK_FORM_SUBMITTED_PROPERTIES.RATING_READ_MORE]: payload.rating_read_more,
          [FEEDBACK_FORM_SUBMITTED_PROPERTIES.FEEDBACK_DIFFICULT]: truncate(payload.feedback_difficult ?? undefined),
          [FEEDBACK_FORM_SUBMITTED_PROPERTIES.FEEDBACK_FEATURES]: truncate(payload.feedback_features ?? undefined),
          [FEEDBACK_FORM_SUBMITTED_PROPERTIES.SOURCE]: payload.source ?? undefined,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['feedback', 'anonymous-list'] });
      setSubmitted(true);
      toast.success('ส่งแบบประเมินเรียบร้อย ขอบคุณครับ');
    } catch {
      toast.error('ส่งไม่สำเร็จ กรุณาลองใหม่');
      setSubmitting(false);
    }
  };

  const handleCopyHashtag = async () => {
    try {
      await navigator.clipboard.writeText(HASHTAG);
      toast.success('คัดลอกแฮชแท็กแล้ว');
    } catch {
      toast.error('คัดลอกไม่สำเร็จ');
    }
  };

  if (submitted) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>ขอบคุณที่ร่วมเป็นส่วนหนึ่งในการพัฒนาแอป</CardTitle>
          <CardDescription>
            เรานำความเห็นไปใช้พัฒนาต่อให้ดียิ่งขึ้นครับ
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border border-primary/20 bg-muted/30 p-4 space-y-2">
            <p className="text-sm font-medium text-foreground">
              ส่งต่อความประทับใจ
            </p>
            <p className="text-sm text-muted-foreground">
              หากพี่น้องแชร์ประสบการณ์การใช้งานลงโซเชียลมีเดีย สามารถตั้งค่าเป็นสาธารณะ และติดแฮชแท็กด้านล่างได้เลย
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-sm bg-muted px-2 py-1 rounded font-mono">
                {HASHTAG}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyHashtag}
                className="gap-1.5"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">
              ติดตามการเดินทางของเรา
            </p>
            <p className="text-sm text-muted-foreground">
              ร่วมเป็นส่วนหนึ่งของชุมชนและรับข่าวสารการอัปเดตได้ที่
            </p>
            <div className="flex flex-wrap gap-1">
              <a
                href={FB_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-border/60 bg-muted/30 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/60 hover:text-primary"
                aria-label="Facebook: Ramadan Life Center"
              >
                <FacebookBrandIcon className="h-4 w-4" />
              </a>
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center  rounded-full border border-border/60 bg-muted/30 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/60 hover:text-primary"
                aria-label="Instagram: ramadan.life.center"
              >
                <InstagramBrandIcon className="h-4 w-4" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>แบบประเมินความพึงพอใจ</CardTitle>
        <CardDescription>
          เราตั้งตารอและอยากรับฟังประสบการณ์การใช้งานแอป เพื่อนำไปพัฒนาให้ดียิ่งขึ้นครับ
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {QUESTIONS.map(({ key, label }) => (
          <div key={key} className="space-y-2">
            <Label className="text-sm font-medium">{label}</Label>
            <div>
              {key === 'rating_overall' && (
                <StarRating value={ratingOverall} onChange={setRatingOverall} showScaleLabels />
              )}
              {key === 'rating_settings' && (
                <StarRating value={ratingSettings} onChange={setRatingSettings} showScaleLabels />
              )}
              {key === 'rating_translation' && (
                <StarRating
                  value={ratingTranslation}
                  onChange={setRatingTranslation}
                  showScaleLabels
                />
              )}
              {key === 'rating_save_progress' && (
                <StarRating
                  value={ratingSaveProgress}
                  onChange={setRatingSaveProgress}
                  showScaleLabels
                />
              )}
              {key === 'rating_recalc_feature' && (
                <StarRating value={ratingRecalc} onChange={setRatingRecalc} showScaleLabels />
              )}
              {key === 'rating_read_more' && (
                <StarRating value={ratingReadMore} onChange={setRatingReadMore} showScaleLabels />
              )}
            </div>
          </div>
        ))}

        <div className="space-y-2">
          <Label htmlFor="feedback_difficult" className="text-sm font-medium">
            พี่น้องเจอจุดที่ใช้งานยาก หรือสับสนตรงไหนบ้างหรือไม่? (ถ้ามี)
          </Label>
          <Textarea
            id="feedback_difficult"
            placeholder="พิมพ์ตอบ..."
            value={feedbackDifficult}
            onChange={(e) => setFeedbackDifficult(e.target.value)}
            rows={3}
            className="resize-none"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="feedback_features" className="text-sm font-medium">
            มีฟีเจอร์อะไรที่พี่น้องอยากให้เพิ่มเข้ามา และพัฒนาให้ดีขึ้นในเวอร์ชันถัดไป? (ถ้ามี)
          </Label>
          <Textarea
            id="feedback_features"
            placeholder="พิมพ์ตอบ..."
            value={feedbackFeatures}
            onChange={(e) => setFeedbackFeatures(e.target.value)}
            rows={3}
            className="resize-none"
          />
        </div>

        <Button
          type="button"
          className="w-full"
          disabled={!canSubmit || submitting}
          onClick={handleSubmit}
        >
          {submitting ? 'กำลังส่ง...' : 'ส่งแบบประเมิน'}
        </Button>
      </CardContent>
    </Card>
  );
}
